import type { SearchGroup } from '@oasis/contracts';
import { Search, X } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import { useApiQuery } from '../api.js';

import {
  ADMINISTRATION,
  BENEVOLE,
  COMPTE,
  domaineInfo,
  domaineOf,
  espaceOf,
  GESTION,
  hubOf,
  rubriquePath,
  type NavEntry,
  type ShellAction,
} from './navigation.js';

/** Espace d'où l'on cherche (l'administration cherche avec la gestion). */
export type SearchEspace = 'benevole' | 'gestion';

/** Un écran ou une action appartient-il à l'espace cherché ? */
const inEspace = (espace: SearchEspace | undefined, e: string | undefined): boolean =>
  espace === undefined || (espace === 'benevole' ? e === 'benevole' : e !== 'benevole');

interface Option {
  id: string;
  label: string;
  detail?: string | undefined;
  link: string;
  category: string;
}

const fold = (v: string): string =>
  v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const matches = (words: readonly string[], parts: readonly (string | undefined)[]): boolean => {
  const text = fold(parts.filter(Boolean).join(' '));
  return words.every((w) => text.includes(w));
};

/**
 * « Aller à » sans passer par les menus : actions (« emprunter », « message »), rubriques
 * et écrans (onglets compris) dont le libellé, la carte ou le domaine contiennent
 * tous les mots tapés. Avec `espace`, seulement ceux de cet espace (retour rc.13 : la
 * recherche du bénévole ne ressort rien de la gestion, et inversement).
 */
export function matchScreens(
  navigation: readonly NavEntry[],
  query: string,
  limit = 6,
  actions: readonly ShellAction[] = [],
  espace?: SearchEspace,
): Option[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const seen = new Set<string>();
  const out: Option[] = [];
  const push = (o: Option): void => {
    if (seen.has(o.link) || out.length >= limit) return;
    seen.add(o.link);
    out.push(o);
  };
  for (const a of actions)
    if (
      !a.path.startsWith('https://') &&
      inEspace(espace, a.espace ?? domaineInfo(a.domaine)?.espace) &&
      matches(words, [a.label, domaineInfo(a.domaine)?.label])
    )
      push({
        id: `action:${a.key}`,
        label: a.label,
        detail: domaineInfo(a.domaine)?.label,
        link: a.path,
        category: 'Écrans',
      });
  for (const d of [...BENEVOLE, COMPTE, ...GESTION, ADMINISTRATION])
    if (
      inEspace(espace, d.espace) &&
      hubOf(d.key, navigation).length > 0 &&
      matches(words, [d.label, d.phrase])
    )
      push({
        id: d.path,
        label: d.label,
        detail: d.espace === 'gestion' ? 'Rubrique de gestion' : 'Rubrique',
        link: rubriquePath(d, navigation),
        category: 'Écrans',
      });
  for (const n of navigation) {
    if (n.available === false || !inEspace(espace, espaceOf(n))) continue;
    const domaine = domaineInfo(domaineOf(n))?.label;
    const carte = n.carte ?? n.group;
    if (!matches(words, [n.label, carte, domaine, n.section])) continue;
    push({
      id: n.path,
      label: carte !== undefined && carte !== n.label ? `${carte} › ${n.label}` : n.label,
      detail: domaine,
      link: n.path,
      category: 'Écrans',
    });
  }
  return out;
}

/**
 * Recherche globale (FR-006) : à partir de deux caractères, résultats groupés par catégorie
 * et déjà limités au périmètre de l'utilisateur par le serveur, dans le seul espace de la
 * page (`espace`, contrôlé aussi par le serveur). Combobox accessible : flèches pour
 * parcourir, Entrée pour ouvrir, Échap pour fermer ; « / » donne le focus.
 */
export function GlobalSearch({
  navigation = [],
  actions = [],
  espace,
  placeholder = 'Rechercher…  ( / )',
  hotkey = true,
  autoFocus = false,
  onClose,
}: {
  navigation?: readonly NavEntry[];
  actions?: readonly ShellAction[];
  /** Espace cherché ; absent : l'espace bénévole côté serveur, tous les écrans côté client. */
  espace?: SearchEspace;
  placeholder?: string;
  /** Touche « / » : donne le focus à ce champ (un seul champ par page). */
  hotkey?: boolean;
  /** Focus à l'ouverture (panneau de la loupe de l'en-tête). */
  autoFocus?: boolean;
  /** Échap ou choix d'un résultat : le panneau qui contient le champ se ferme. */
  onClose?: () => void;
}): ReactElement {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(text.trim());
    }, 250);
    return () => {
      clearTimeout(t);
    };
  }, [text]);

  useEffect(() => {
    if (!hotkey) return;
    const focus = (e: globalThis.KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (e.key === '/' && target?.tagName !== 'INPUT' && target?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', focus);
    return () => {
      window.removeEventListener('keydown', focus);
    };
  }, [hotkey]);

  const results = useApiQuery<{ groups: SearchGroup[] }>(
    query.length >= 2
      ? `/search?q=${encodeURIComponent(query)}&limit=20${espace === undefined ? '' : `&espace=${espace}`}`
      : null,
  );
  const screens = useMemo(
    () => (query.length >= 2 ? matchScreens(navigation, query, 6, actions, espace) : []),
    [navigation, query, actions, espace],
  );
  const groups = useMemo(
    () => [
      ...(screens.length > 0 ? [{ category: 'Écrans', items: screens }] : []),
      ...(results.data?.groups ?? []).map((g) => ({
        category: g.category,
        items: g.items.map((item): Option => ({
          id: item.id,
          label: item.label,
          detail: item.detail,
          link: item.link,
          category: g.category,
        })),
      })),
    ],
    [screens, results.data],
  );
  const options = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const go = (link: string): void => {
    setOpen(false);
    setText('');
    onClose?.();
    void navigate(link);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      setOpen(false);
      onClose?.();
      return;
    }
    if (options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + options.length) % options.length);
    } else if (e.key === 'Enter') {
      const option = options[active];
      if (option !== undefined) go(option.link);
    }
  };

  const showList = open && query.length >= 2;
  return (
    <div className="o-search">
      <Search aria-hidden="true" size={16} className="o-search__icon" />
      <input
        ref={input}
        className="o-search__input"
        type="search"
        role="combobox"
        aria-label="Rechercher un écran, une personne, une activité, un organisme…"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          showList && options[active] !== undefined ? `${listId}-${String(active)}` : undefined
        }
        placeholder={placeholder}
        autoFocus={autoFocus}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => {
            setOpen(false);
          }, 150);
        }}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <div className="o-search__results" id={listId} role="listbox" aria-label="Résultats">
          {results.isPending && <p className="o-hint">Recherche…</p>}
          {results.isSuccess && options.length === 0 && <p className="o-hint">Aucun résultat.</p>}
          {groups.map((group) => (
            <div key={group.category} role="group" aria-label={group.category}>
              <p className="o-search__group-title">{group.category}</p>
              {group.items.map((item) => {
                const index = options.findIndex(
                  (o) => o.id === item.id && o.category === group.category,
                );
                return (
                  <a
                    key={item.id}
                    id={`${listId}-${String(index)}`}
                    role="option"
                    aria-selected={index === active}
                    href={item.link}
                    className="o-search__option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      go(item.link);
                    }}
                  >
                    {item.label}
                    {item.detail !== undefined && (
                      <span className="o-search__detail">{item.detail}</span>
                    )}
                  </a>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ESPACE_LIBELLE: Record<SearchEspace, string> = {
  benevole: 'Recherche dans l’espace bénévole',
  gestion: 'Recherche dans l’espace gestion',
};

/**
 * Loupe de l'en-tête (retour du référent sur la rc.13 : en gestion, le champ de recherche
 * disparaissait sous le bouton d'espace). Un bouton compact ouvre un panneau de recherche
 * plein largeur, au clavier (« / » ou Ctrl+K) comme au toucher ; Échap ou un résultat le
 * ferme. L'espace cherché est écrit en tête du panneau.
 */
export function HeaderSearch({
  navigation,
  actions,
  espace,
}: {
  navigation: readonly NavEntry[];
  actions: readonly ShellAction[];
  espace: SearchEspace;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <>
      <button
        ref={button}
        type="button"
        className="o-header__search-button"
        aria-label="Rechercher (/ ou Ctrl+K)"
        title="Rechercher (/ ou Ctrl+K)"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <Search aria-hidden="true" size={20} />
      </button>
      {open &&
        createPortal(
          <div
            className="o-search-panel"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div
              id={panelId}
              className="o-search-panel__box"
              role="dialog"
              aria-modal="true"
              aria-label={ESPACE_LIBELLE[espace]}
            >
              <div className="o-search-panel__head">
                <p className="o-search-panel__title">{ESPACE_LIBELLE[espace]}</p>
                <button
                  type="button"
                  className="o-search-panel__close"
                  aria-label="Fermer la recherche"
                  onClick={close}
                >
                  <X aria-hidden="true" size={18} />
                </button>
              </div>
              <GlobalSearch
                navigation={navigation}
                actions={actions}
                espace={espace}
                placeholder={
                  espace === 'gestion'
                    ? 'Une personne, une activité, un lot, une demande…'
                    : 'Un produit, une de mes activités, une commande…'
                }
                hotkey={false}
                autoFocus
                onClose={close}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
